import { AlertTriangle, ArrowRightLeft, CheckCircle2, KeyRound, RotateCw, X } from 'lucide-react'
import { formatRelativeSoon } from '@/lib/account-dates'
import { getRelativeTime } from '@/components/DataFreshnessIndicator'
import { cn } from '@/lib/utils'

// The state a broken PMS connection puts the operator in, turned from a dead end into a path.
//
// WHAT THIS REPLACES: a single fatalistic line — "Automatic retry in about 48 minutes. Update
// the secret to recover sooner." It named one remedy (often the wrong one), gave no diagnosis,
// and read as "wait". A hotel that arrived in this business carrying a suspended connection
// left its operator with nothing to do but open a support ticket.
//
// WHAT IT SAYS NOW: what broke, who it is hurting, what the evidence is, and the two or three
// things that actually fix it — with the automatic retry demoted to a safety net ("you don't
// have to wait for it") instead of the only offer on the table.

type Tone = 'degraded' | 'suspended'

interface ConnectionRecoveryPanelProps {
  tone: Tone
  consecutiveAuthFailures: number
  suspendedAt: string | null
  lastProbeAt: string | null
  nextProbeEstimate: string | null
  /** Properties in THIS business served by the connection — the people feeling the outage. */
  propertyNames: string[]
  /** Owner/admin on a connection this business actually owns: the full remedy list. */
  canManage: boolean
  /** There is at least one other owned connection to move onto. */
  canMove: boolean
}

// Oxford-comma-free list: 'Seaside Hotel', 'Seaside Hotel and Mountain Lodge',
// 'Seaside Hotel, Mountain Lodge and Harbour Rooms'.
function joinNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? ''
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

export default function ConnectionRecoveryPanel({
  tone,
  consecutiveAuthFailures,
  suspendedAt,
  lastProbeAt,
  nextProbeEstimate,
  propertyNames,
  canManage,
  canMove,
}: ConnectionRecoveryPanelProps) {
  const suspended = tone === 'suspended'
  const meta: string[] = []
  if (consecutiveAuthFailures > 0) {
    meta.push(`${consecutiveAuthFailures} failed sign-in${consecutiveAuthFailures === 1 ? '' : 's'}`)
  }
  if (suspended && suspendedAt) meta.push(`paused ${getRelativeTime(suspendedAt)}`)
  if (lastProbeAt) meta.push(`last checked ${getRelativeTime(lastProbeAt)}`)

  return (
    <div
      className={cn(
        'border-b border-border/40 px-5 py-4',
        suspended ? 'bg-rose-50/60 text-rose-700' : 'bg-amber-50/60 text-amber-700'
      )}
    >
      <div className="flex items-start gap-2">
        {suspended ? (
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
        ) : (
          <RotateCw className="mt-0.5 h-4 w-4 shrink-0" />
        )}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold">
            {suspended ? 'Your PMS is refusing this sign-in' : 'This connection is being re-checked'}
          </p>

          <p className="mt-0.5 text-xs">
            {propertyNames.length === 0
              ? 'No properties in this business are using it right now, so nothing is affected.'
              : suspended
                ? `Rate updates for ${joinNames(propertyNames)} are paused until it works again.`
                : `${joinNames(propertyNames)} keep pricing normally while we confirm it.`}
          </p>

          {meta.length > 0 && (
            <p className={cn('mt-1.5 text-[11px] tabular-nums', suspended ? 'text-rose-500' : 'text-amber-600')}>
              {meta.join(' · ')}
            </p>
          )}

          {/* The remedies, ordered cheapest-first. Each maps to a button in the action bar
              below, so the operator never has to guess which control does which. */}
          {canManage && suspended && (
            <ul className="mt-3 space-y-1.5 border-t border-rose-200/70 pt-3">
              <li className="flex items-start gap-2 text-xs">
                <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-rose-400" />
                <span>
                  Working again? <span className="font-semibold">Test connection</span> puts it straight back to work
                  — no waiting.
                </span>
              </li>
              <li className="flex items-start gap-2 text-xs">
                <KeyRound className="mt-0.5 h-3.5 w-3.5 shrink-0 text-rose-400" />
                <span>
                  Secret changed in your PMS? <span className="font-semibold">Update secret</span> with the new one.
                </span>
              </li>
              {canMove && (
                <li className="flex items-start gap-2 text-xs">
                  <ArrowRightLeft className="mt-0.5 h-3.5 w-3.5 shrink-0 text-rose-400" />
                  <span>
                    Already have a connection that works? <span className="font-semibold">Move properties</span> onto
                    it — no secret to retype.
                  </span>
                </li>
              )}
            </ul>
          )}

          {/* The automatic probe: a safety net, explicitly NOT the thing to wait for. */}
          <p className={cn('mt-3 text-[11px]', suspended ? 'text-rose-600' : 'text-amber-600')}>
            {nextProbeEstimate
              ? `We keep retrying on our own — the next automatic check runs ${formatRelativeSoon(nextProbeEstimate)}. You don't have to wait for it.`
              : "We're re-checking this automatically in the background."}
          </p>
        </div>
      </div>
    </div>
  )
}

interface ConnectionHealedPanelProps {
  /** Verbatim server copy — the moment the operator has been waiting for. */
  message: string
  propertyNames: string[]
  onDismiss: () => void
}

// The resolution. A heal is not a toast: it is the end of an outage, so it lands ON the card
// that was broken, in the same place the failure was, and stays until the operator dismisses it.
export function ConnectionHealedPanel({ message, propertyNames, onDismiss }: ConnectionHealedPanelProps) {
  return (
    <div className="animate-slide-in border-b border-border/40 bg-emerald-50/70 px-5 py-4">
      <div className="flex items-start gap-2">
        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-emerald-800">{message}</p>
          {propertyNames.length > 0 && (
            <p className="mt-0.5 text-xs text-emerald-700">
              {joinNames(propertyNames)} will pick up your latest rates on the next cycle.
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="-mr-1 rounded-lg p-1 text-emerald-600 transition-colors hover:bg-emerald-100 hover:text-emerald-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  )
}
